// frontend/src/pages/CadastroPage/CadastroPage.jsx

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Form, Input, Button, Card, Typography, Alert, Image, Checkbox,
} from 'antd';
import { MailOutlined, LockOutlined, UserOutlined, PhoneOutlined } from '@ant-design/icons';
import { GoogleLoginButton, hasGoogleOAuth } from '../../components/GoogleLoginButton/GoogleLoginButton';
import ReenviarConfirmacao from '../../components/ReenviarConfirmacao/ReenviarConfirmacao';
import logo from '../../assets/images/logo_vertical.jpg';
import { getErrorMessage } from '../../utils/errorHandler';
import { validatePassword } from '../../utils/passwordValidation';
import { getDocumentoVigente } from '../../services/legalService';
import { irAposLogin } from '../../utils/legalAccess';
import { trackMetaEvent } from '../../utils/metaPixel';
import { obterOrigemCadastro } from '../../utils/origemCadastro';
import {
  buildAuthQuery,
  isPerfilPrestadorParam,
  setPrestadorIntent,
} from '../../utils/prestadorIntentStorage';
import '../../styles/auth.css';

const { Title, Text } = Typography;

function CadastroPage() {
  const [error, setError] = useState('');
  // E-mail que já tem cadastro: pode ser alguém que não confirmou e voltou para se cadastrar de novo.
  const [emailJaCadastrado, setEmailJaCadastrado] = useState('');
  // Possível erro de digitação no e-mail: { sugestao, digitado, mensagem, jaConfirmado }
  const [emailSugerido, setEmailSugerido] = useState(null);
  const [form] = Form.useForm();
  const [successVerify, setSuccessVerify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [legalDocs, setLegalDocs] = useState(null);
  const [legalLoadError, setLegalLoadError] = useState('');
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref') || '';
  const perfilPrestador = isPerfilPrestadorParam(searchParams.get('perfil'));

  useEffect(() => {
    if (perfilPrestador) setPrestadorIntent();
  }, [perfilPrestador]);

  // Retoma um destino interno após o cadastro (ex.: /convite/:token).
  useEffect(() => {
    const redirect = searchParams.get('redirect');
    if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
      try { sessionStorage.setItem('redirect_apos_login', redirect); } catch { /* ignore */ }
    }
  }, [searchParams]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      auth.logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [termos, privacidade] = await Promise.all([
          getDocumentoVigente('termos'),
          getDocumentoVigente('privacidade'),
        ]);
        if (!cancelled) setLegalDocs({ termos, privacidade });
      } catch {
        if (!cancelled) setLegalLoadError('Não foi possível carregar os termos. Tente novamente em instantes.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onFinish = async (values, { confirmarEmail = false } = {}) => {
    setError('');
    setEmailJaCadastrado('');
    setEmailSugerido(null);
    setSuccessVerify(null);
    setLoading(true);
    try {
      if (!legalDocs) {
        setError(legalLoadError || 'Aguarde o carregamento dos termos legais.');
        return;
      }
      const result = await auth.register(values.email, values.password, values.nome_completo, {
        telefone: values.telefone?.trim() || undefined,
        ref: ref || undefined,
        origem: obterOrigemCadastro(searchParams),
        aceite_termos_versao: legalDocs.termos.versao,
        aceite_privacidade_versao: legalDocs.privacidade.versao,
        confirmar_email: confirmarEmail,
      });
      if (result?.verificationSent && result?.email) {
        trackMetaEvent('CompleteRegistration', { content_name: 'cadastro_email_verificacao' });
        setSuccessVerify({ message: result.detail || 'Enviamos um e-mail de confirmação.', email: result.email });
        return;
      }
      if (result?.user) {
        trackMetaEvent('CompleteRegistration', { content_name: 'cadastro_direto' });
        irAposLogin(result.user, navigate);
        return;
      }
      trackMetaEvent('CompleteRegistration', { content_name: 'cadastro_direto' });
      navigate('/meu-desempenho');
    } catch (err) {
      const msg = err.message || getErrorMessage(err, 'Não foi possível criar a conta. Tente novamente.');
      if (err.emailSugerido) {
        setEmailSugerido({
          sugestao: err.emailSugerido,
          digitado: values.email,
          mensagem: msg.replace(/^Email:\s*/, ''),
          jaConfirmado: confirmarEmail,
        });
      } else if (msg.toLowerCase().includes('já existe') || msg.toLowerCase().includes('already') || msg.toLowerCase().includes('registered')) {
        setError('Este e-mail já está cadastrado. Faça login ou use outro e-mail.');
        setEmailJaCadastrado(values.email.trim());
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const usarEmailSugerido = () => {
    form.setFieldsValue({ email: emailSugerido.sugestao });
    setEmailSugerido(null);
    form.submit();
  };

  const manterEmailDigitado = () => {
    onFinish(form.getFieldsValue(true), { confirmarEmail: true });
  };

  const handleGoogleSuccess = async (tokenResponse) => {
    setError('');
    setLoadingGoogle(true);
    try {
      const userData = await auth.loginWithGoogle(
        tokenResponse.access_token,
        ref || undefined,
        obterOrigemCadastro(searchParams),
      );
      irAposLogin(userData, navigate);
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível entrar com o Google.'));
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleGoogleError = () => {
    setError('Login com Google cancelado ou falhou.');
    setLoadingGoogle(false);
  };

  return (
    <div className="auth-shell">
      <Card className="auth-card auth-card--wide">
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Image
            width={140}
            src={logo}
            alt="Logo Saldo Ativo"
            preview={false}
            className="auth-logo"
          />
        </div>

        <Title level={4} className="auth-title" style={{ textAlign: 'center' }}>
          Criar conta
        </Title>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 16 }}>
          Preencha seus dados para começar a usar o Saldo Ativo.
        </Text>

        {!successVerify && (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            message="30 dias grátis"
            description="Você testa o Saldo Ativo completo por 30 dias, sem cartão de crédito. Depois é só assinar se quiser continuar."
          />
        )}

        {perfilPrestador && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Ferramentas de prestador"
            description="Após entrar, vamos perguntar se você quer ativar Clientes e o painel A receber."
          />
        )}
        {ref && (
          <Alert
            message="Indicação ativa"
            description="Sua conta será vinculada a quem indicou. Os 30 dias grátis valem normalmente."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
        {emailJaCadastrado && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
              Já se cadastrou antes mas nunca recebeu (ou perdeu) o e-mail de confirmação?
            </Text>
            <ReenviarConfirmacao email={emailJaCadastrado} block />
          </div>
        )}
        {emailSugerido && !successVerify && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={emailSugerido.mensagem}
            description={
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                <Button type="primary" size="small" onClick={usarEmailSugerido} disabled={loading}>
                  Usar {emailSugerido.sugestao}
                </Button>
                {!emailSugerido.jaConfirmado && (
                  <Button size="small" onClick={manterEmailDigitado} disabled={loading}>
                    Não, {emailSugerido.digitado} está certo
                  </Button>
                )}
              </div>
            }
          />
        )}
        {successVerify && (
          <>
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message="Conta criada"
              description={
                <>
                  <div style={{ marginBottom: 8 }}>{successVerify.message}</div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    Enviamos o link para <strong>{successVerify.email}</strong>. Clique no link do e-mail para ativar sua conta: você entra direto no Saldo Ativo.
                  </div>
                </>
              }
            />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8, textAlign: 'center' }}>
                Não chegou em alguns minutos? Veja as pastas Spam e Promoções, ou peça outro link:
              </Text>
              <ReenviarConfirmacao email={successVerify.email} block />
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <Text type="secondary">E-mail digitado errado? </Text>
                <Link to="/cadastro" onClick={() => setSuccessVerify(null)}>Criar com outro e-mail</Link>
              </div>
            </div>
          </>
        )}

        {!successVerify && hasGoogleOAuth && (
          <div style={{ marginBottom: 20 }}>
            <GoogleLoginButton
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              loading={loadingGoogle}
              disabled={loading}
              block
              size="large"
            />
          </div>
        )}

        {!successVerify && (
        <Form
          form={form}
          name="cadastro"
          onFinish={(values) => onFinish(values)}
          onValuesChange={(changed) => {
            if ('email' in changed) setEmailSugerido(null);
          }}
          layout="vertical"
          size="large"
          autoComplete="off"
        >
          <Form.Item
            name="nome_completo"
            label="Nome completo"
            rules={[{ required: true, message: 'Informe seu nome completo' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="Como deseja ser chamado" />
          </Form.Item>

          <Form.Item
            name="email"
            label="E-mail"
            rules={[
              { required: true, message: 'Informe seu e-mail' },
              { type: 'email', message: 'E-mail inválido' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="seu@email.com" />
          </Form.Item>

          <Form.Item
            name="telefone"
            label="Telefone (opcional)"
          >
            <Input prefix={<PhoneOutlined />} placeholder="(00) 00000-0000" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Senha"
            validateTrigger={['onBlur', 'onChange']}
            rules={[
              { required: true, message: 'Crie uma senha' },
              { min: 8, message: 'Mínimo de 8 caracteres' },
              {
                validator: (_, value) => {
                  const err = validatePassword(value);
                  return err ? Promise.reject(new Error(err)) : Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Mínimo 8 caracteres" />
          </Form.Item>


          {legalLoadError && (
            <Alert type="warning" message={legalLoadError} showIcon style={{ marginBottom: 16 }} />
          )}
          <Form.Item
            name="aceite_legal"
            valuePropName="checked"
            rules={[
              {
                validator: (_, v) => (v ? Promise.resolve() : Promise.reject(new Error('Aceite os termos para continuar'))),
              },
            ]}
          >
            <Checkbox disabled={!legalDocs}>
              Li e aceito os{' '}
              <Link to="/termos" target="_blank" rel="noopener noreferrer">Termos de Uso</Link>
              {legalDocs && <> (v{legalDocs.termos.versao})</>}
              {' '}e a{' '}
              <Link to="/privacidade" target="_blank" rel="noopener noreferrer">Política de Privacidade</Link>
              {legalDocs && <> (v{legalDocs.privacidade.versao})</>}.
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              size="large"
              disabled={!legalDocs}
            >
              Criar conta
            </Button>
          </Form.Item>
        </Form>
        )}

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Text type="secondary">Já tem conta? </Text>
          <Link to={`/login${buildAuthQuery({ ref, perfilPrestador })}`}>Fazer login</Link>
        </div>
      </Card>
    </div>
  );
}

export default CadastroPage;
